import { appConfig } from '../../../appConfig';
import type {
    ApiRecipe,
    ApiRecipeComment,
    ApiRecipeIngredient,
    ApiRecipePart,
} from '../../../models/recipe/recipeModel';
import { Helpers } from '../../../utility/helpers';
import type {
    CookingInstructions,
    ImageSticker,
    Ingredient,
    RecipeCategory,
    RecipeComment,
    RecipePart,
    RecipeProps,
} from '../../recipe/models';
import type { ApiGastrofyRecipe } from '../api/gastrofy/responses/gastrofyResponses';

export const isNortforkRecipeFoodBox = (recipe: ApiGastrofyRecipe) => {
    if (!recipe.tags) {
        return false;
    }

    const foodBagTagId = appConfig.coopSettings.recipe.foodBoxTagId;

    if (!foodBagTagId) {
        return false;
    }

    return recipe.tags.some((gt) => gt.id === foodBagTagId);
};

const getStickerUrl = (keyword?: string, imageStickers?: ImageSticker[]) => {
    if (!keyword) return undefined;
    if (!imageStickers) return undefined;

    const sticker = imageStickers
        .filter((_sticker) => !!_sticker.keyword)
        .find(
            (_sticker) => _sticker.keyword.trim().toLowerCase() === keyword.trim().toLowerCase(),
        )?.imageUrl;

    return sticker;
};

export const mapToIngredientProps = (ingredients: ApiRecipeIngredient[]) => {
    const ingredientProps = ingredients.map<Ingredient>((ingredient) => {
        const displayName = ingredient.usePlural
            ? ingredient.ingredient.pluralName
            : ingredient.ingredient.singularName;
        const { sequenceNumber } = ingredient;
        let friendlyText = `${ingredient.prePreparation || ''} ${displayName} ${
            ingredient.postPreparation || ''
        }`.trim();

        friendlyText = friendlyText.replace(/\s+(?=[,.])/g, '');

        let quantityFromattedText: string | null = null;
        const parsedQuantity = parseFloat(ingredient.quantity);
        if (!Number.isNaN(parsedQuantity) && parsedQuantity > 0) {
            quantityFromattedText = Helpers.formatNumber(
                parseFloat(ingredient.quantity).toString(),
                undefined,
                ',',
            );
        }

        // if halv of something, show 1/2 instead of 0.5. (0,5 = 1/2, 1,5 = 1 1/2)
        if (parsedQuantity % 1 === 0.5) {
            quantityFromattedText = `${parsedQuantity >= 1 ? `${parsedQuantity - 0.5} ` : ''} 1/2`;
        }

        return {
            friendlyText,
            formattedQuantity: quantityFromattedText || '',
            unit: ingredient.unit,
            sequenceNumber,
        };
    });

    return ingredientProps;
};

const mapToRecipePartProps = (recipeParts: ApiRecipePart[]) => {
    const recipePartProps = recipeParts.map<RecipePart>((recipePart) => ({
        name: recipePart.name,
        ingredients: mapToIngredientProps(recipePart.ingredients),
    }));
    return recipePartProps;
};

export const mapToRecipeCookingInstructions = (recipeCookingInstructions: string) => {
    const regexTitleStart = /<h3>/g;
    const regexAllTags = /(<([^>]+)>)/gi;
    const cookingInstructions: CookingInstructions[] = [];

    if (recipeCookingInstructions) {
        const instructions = recipeCookingInstructions.split(regexTitleStart);
        let instructionTitle = '';
        let instruction = '';

        instructions.forEach((value, index) => {
            const regexTitleEnd = /<\/h3>/g; // this needs to be inside foreach to work for all items.
            if (regexTitleEnd.test(value)) {
                const titleAndInstruction = value.split(regexTitleEnd);
                instructionTitle = titleAndInstruction[0].replace(regexAllTags, '');
                instruction = titleAndInstruction[1].replace(regexAllTags, '');
                cookingInstructions.push({ title: instructionTitle, instructions: [instruction] });
            } else {
                instruction = value.replace(regexAllTags, '');

                // If first is missing title
                if (index === 0) {
                    cookingInstructions.push({
                        title: instructionTitle,
                        instructions: instruction ? [instruction] : [],
                    });
                } else {
                    cookingInstructions[cookingInstructions.length - 1].instructions.push(
                        instruction,
                    );
                }
            }
        });
    }

    return cookingInstructions;
};

type RecipeMainCategoryNames =
    | 'Typ av rätt'
    | 'Måltid'
    | 'Ingrediens'
    | 'Tillagningssätt'
    | 'Tillfälle'
    | 'Världens mat'
    | 'Specialkost';

export const getRecipeSubCategories = (
    categoryName: RecipeMainCategoryNames,
    recipeCategories: RecipeCategory[],
) => {
    const categories =
        recipeCategories?.filter(
            (recipeCategory) =>
                !!recipeCategory.categories?.length &&
                recipeCategory.categories[0] === categoryName,
        ) || [];

    return categories;
};

export const getRecipeSubCategoryNames = (
    categoryName: RecipeMainCategoryNames,
    categories: RecipeCategory[],
) => {
    const recipeSubCategories = getRecipeSubCategories(categoryName, categories);
    const subCategoryNames = recipeSubCategories.map((x) => x.categories[x.categories.length - 1]);

    return subCategoryNames;
};

const getMinutesFromTimeParts = (timeDigit?: string, timeUnit?: string) => {
    const digit = timeDigit ? parseFloat(timeDigit.replace(',', '.')) : NaN;
    let timeInMinutes = 0;

    if (!Number.isNaN(digit) && timeUnit) {
        const unit = timeUnit.toLowerCase();

        if (unit.startsWith('min')) {
            timeInMinutes = digit;
        }

        if (unit.startsWith('tim')) {
            timeInMinutes = digit * 60; // timeDigit is an hour.
        }

        if (unit.startsWith('dag') || unit.startsWith('dygn')) {
            timeInMinutes = digit * 60 * 24; // timeDigit is an day.
        }

        if (unit.startsWith('veck')) {
            timeInMinutes = digit * 60 * 24 * 7; // timeDigit is an week.
        }
    }

    return timeInMinutes;
};

export const getCookingTimeInMinutes = (cookingTime: string) => {
    const cookingOneTimeRegex =
        /^(?<time>[\d.,]+)\s?(?<unit>veckor|vecka|dagar|dag|dygn|timmar|timme|tim|minuter|minut|min)(\s?\+.*)?$/;

    const cookingTwoTimeRegExp =
        /^(?<time>[\d.,]+)\s?(?<unit>veckor|vecka|dagar|dag|dygn|timmar|timme|tim|minuter|minut|min)([\s+]|och)*(?<time2>[\d.,]+)\s?(?<unit2>veckor|vecka|dagar|dag|dygn|timmar|timme|tim|minuter|minut|min)(\s?\+.*)?$/;

    const cookingThreeTimeRegExp =
        /^(?<time>[\d.,]+)\s?(?<unit>veckor|vecka|dagar|dag|dygn|timmar|timme|tim|minuter|minut|min)([\s+]|och)*(?<time2>[\d.,]+)\s?(?<unit2>veckor|vecka|dagar|dag|dygn|timmar|timme|tim|minuter|minut|min)([\s+]|och)*(?<time3>[\d.,]+)\s?(?<unit3>veckor|vecka|dagar|dag|dygn|timmar|timme|tim|minuter|minut|min)(\s?\+.*)?$/;

    if (!cookingTime) {
        return 0;
    }

    let timeMatch = cookingThreeTimeRegExp.exec(cookingTime.trim());

    if (!timeMatch) {
        timeMatch = cookingTwoTimeRegExp.exec(cookingTime.trim());

        if (!timeMatch) {
            timeMatch = cookingOneTimeRegex.exec(cookingTime.trim());
        }
    }
    if (!timeMatch || !timeMatch.groups) {
        return 0;
    }

    const totalTimeInMinutes =
        getMinutesFromTimeParts(timeMatch.groups.time, timeMatch.groups.unit) +
        getMinutesFromTimeParts(timeMatch.groups.time2, timeMatch.groups.unit2) +
        getMinutesFromTimeParts(timeMatch.groups.time3, timeMatch.groups.unit3);
    return totalTimeInMinutes;
};

export const mapToComments = (comments: ApiRecipeComment[]) => {
    const domainComments =
        comments?.map<RecipeComment>((comment) => ({
            id: comment.id,
            recipeId: comment.recipeId,
            name: comment.name,
            comment: comment.comment,
            isAdmin: comment.isAdmin,
            date: new Date(comment.date),
        })) || [];

    return domainComments;
};

export const mapToRecipeProps = (recipe: ApiRecipe, stickers?: ImageSticker[]) => {
    if (!recipe) return undefined;

    const numberOfAllIngredients = recipe.recipePart.reduce((prev, next) => {
        return prev + next.ingredients.length;
    }, 0);

    const recipeProps: RecipeProps = {
        id: recipe.id,
        externalId: recipe.externalId.toString(),
        name: recipe.name,
        imageUrl: recipe.imageUrl,
        portions: recipe.yieldValue,
        portionsUnit: recipe.yieldUnit,
        parts: mapToRecipePartProps(recipe.recipePart),
        stickerUrl: recipe.keywords && stickers && getStickerUrl(recipe.keywords, stickers),
        averageRating: recipe.avgRating,
        ratingPercent: recipe.avgRating > 0 ? Math.ceil(recipe.avgRating * 2 * 10) : 0,
        ratingCount: recipe.numberOfVotes,
        preamble: recipe.preamble,
        climateImpactKg: recipe.climateImpactKg,
        ovenTemperature: recipe.ovenTemperature,
        cookingTime: recipe.totalTime ? recipe.totalTime : recipe.cookingTime,
        prepTime: recipe.preparationTime,
        totalTime: recipe.totalTime,
        cookingInstructions: mapToRecipeCookingInstructions(recipe.cookingInstructions),
        servingTips: recipe.servingTips,
        tips: recipe.tips,
        numberOfIngredients: numberOfAllIngredients,
        recipeCreator: recipe.recipeCreator,
        categories: recipe.categories,
        comments: mapToComments(recipe.comments),
        isFoodBox: recipe.isFoodBox,
    };

    return recipeProps;
};
